/**
 * The board's Total line (19/10/2026), under the last row and on the same
 * grid: the containers shown, the different lists and list groups of the day,
 * the drops planned. A second, muted line says how many of them really get a
 * drop when some don't (skipped, stopped or not planned). Until the day plan
 * answers, its lists, groups and drops are placeholders (23/10/2026).
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, ReactNode } from 'react';

import { Skeleton } from '@/components/ui';

import { BOARD_GRID } from '../ContainerRow';
import { useBoardPreviewState } from '../day-plan/board-preview-state';
import type { BoardTotals } from './board-totals';

const count = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

const Cell: FC<{ main: ReactNode; sub?: string | null; title: string; mono?: boolean }> = ({ main, sub, title, mono }) => (
  <span className={`flex min-w-0 flex-col gap-0.5 ${mono ? 'font-mono tabular-nums' : ''}`} title={title}>
    <span className="truncate text-xs font-semibold text-foreground">{main}</span>
    {sub ? <span className="truncate text-[10px] text-muted-foreground">{sub}</span> : null}
  </span>
);

const withDrop = (some: number, all: number) => (some === all ? null : `${some} with a drop`);

/** The day plan has not answered: shimmer while loading, "—" when it failed. */
const Pending: FC<{ failed: boolean }> = ({ failed }) =>
  failed ? <span className="text-xs text-muted-foreground">—</span> : <Skeleton className="h-3 w-20" data-testid="pending-total" />;

export const BoardTotalsRow: FC<{ totals: BoardTotals }> = ({ totals: t }) => {
  const plan = useBoardPreviewState();
  if (plan !== 'ready') return <TotalsLine t={t} pending={<Pending failed={plan === 'failed'} />} />;
  return <TotalsLine t={t} />;
};

const TotalsLine: FC<{ t: BoardTotals; pending?: ReactNode }> = ({ t, pending }) => (
  <div className={`${BOARD_GRID} border-t border-border bg-muted/40 py-2.5`}>
    <span />
    <span />
    <span className="flex min-w-0 flex-col gap-0.5">
      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Total</span>
      <span className="truncate text-xs">
        {count(t.containers, 'container', 'containers')}
        {t.on < t.containers ? ` · ${t.on} on` : null}
      </span>
    </span>
    <span />
    {pending ?? (
      <Cell
        main={count(t.lists, 'list', 'lists')}
        sub={withDrop(t.listsWithDrops, t.lists)}
        title={`Different lists of the day in these rows — a list two containers share counts once. ${t.listsWithDrops} of them get at least one drop today.`}
      />
    )}
    {pending ?? (
      <Cell
        main={count(t.groups, 'list group', 'list groups')}
        sub={withDrop(t.groupsWithDrops, t.groups)}
        title={`Different list groups of the day in these rows — one two containers share counts once. ${t.groupsWithDrops} of them get at least one drop today.`}
      />
    )}
    {pending ?? <Cell mono main={`${t.planned} planned`} sub={t.generated ? `${t.sent} sent` : null} title="Today's drops of these rows, as Progress counts them — skipped and stopped drops are out." />}
    <span />
    <span />
  </div>
);
